import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { InspectionService } from '../../../inspection.service';
import { VersionService } from '../../../../version/version.service';
import { UserService } from '../../../../user/user.service';
import { InspectorService } from '../../../../inspector/inspector.service';
import { DialogService } from '../../../../dialog/dialog.service';
import { ToastrService } from 'ngx-toastr';
import { Answer, Inspection, InspectionItem, ItemInfo, User } from '../../../../shared/interfaces';
import { orderBy, uniqBy } from 'lodash';
import * as moment from 'moment';

export interface JournalNote {
  id?: number;
  inspectionId?: number;
  date?: Date;
  type: string;
  note: string;
  user?: User | string;
  reason?: string;
  itemIndex?: number;
  sectionIndex?: number;
  questionIndex?: number;
  questionId?: number;
  groupId?: number;
  flagId?: number;
}

@Component({
  selector: 'app-journal-sidebar',
  templateUrl: './journal-sidebar.component.html',
  styleUrls: ['./journal-sidebar.component.scss'],
})
export class JournalSidebarComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() inspectionId: number;
  @Output() closePanel = new EventEmitter<void>();

  inspection: Inspection;
  answers: Answer[];
  items: Array<InspectionItem & { info?: ItemInfo; index?: number }> = [];
  allFlags = [];
  allComments = [];
  inspectors: any[] = [];

  allNotes: JournalNote[] = [];
  filteredNotes: JournalNote[] = [];
  showAddModal = false;
  showFilterDropdown = false;
  selectedTypes: string[] = ['journal'];
  isLoading = false;
  expandedNotes: Set<string> = new Set();
  editingNote: JournalNote | null = null;
  currentUser: User;

  noteTypes = [
    { label: 'Journal Notes', type: 'journal', count: 0 },
    { label: 'Scheduling Notes', type: 'scheduling', count: 0 },
    { label: 'Inspection Created Notes', type: 'inspection', count: 0 },
    { label: 'Question Comments/Notes', type: 'answer', count: 0 },
    { label: 'Flag Notes', type: 'flag', count: 0 },
    { label: 'Flag Group Internal Notes', type: 'flag-group', count: 0 },
    { label: 'Flag Internal Notes', type: 'flag-internal', count: 0 },
    { label: 'Underwriting Review Comments', type: 'flag-review', count: 0 },
  ];

  constructor(
    private inspectionService: InspectionService,
    private versionService: VersionService,
    private userService: UserService,
    private inspectorService: InspectorService,
    private dialog: DialogService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible && this.inspectionId) {
      this.loadAllNotes();
    }
  }

  async loadAllNotes(): Promise<void> {
    this.isLoading = true;
    try {
      this.allNotes = [];
      this.currentUser = await this.userService.profile();
      this.inspection = await this.inspectionService.get(this.inspectionId);
      await this.loadInspectors();

      this.answers = await this.inspectionService.answers(this.inspection.id);
      await this.loadItems();
      await this.mergeAnswers();
      this.getFlagsAndComments();

      await this.createInspectionNote();
      this.createAnswerNotes();
      this.createFlagNotes();
      this.createFlagGroupNotes();
      this.createUnderwritingReviewNotes();
      await this.loadNotesFromApi();

      this.arrangeNotes();
    } catch (e) {
      console.error('Error loading notes:', e);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  // --- Data loading (same as InspectionNotesComponent) ---

  private async loadInspectors(): Promise<void> {
    try {
      const response: any = await this.inspectorService.list({ isPagination: 0 }).toPromise();
      const data = response[0];
      this.inspectors = Array.isArray(data) ? data : [data];
    } catch (e) {
      this.inspectors = [];
    }
  }

  private async loadItems(): Promise<void> {
    this.items = [];
    this.versionService.treeCache = [];
    const items = (await this.versionService.tree(this.inspection.versionId)).items;
    items.forEach(item => {
      item.sections = item.sections.filter(section => section.questions.length);
    });
    items.forEach((item: any, i) => {
      if (!item.sections.length) {
        delete items[i];
      } else {
        item.info = this.inspection.items.find(info => info.itemId === item.id);
      }
    });
    items.forEach((item: InspectionItem & { info: ItemInfo }) => {
      if (!item.info) return;
      if (item.info.quantity === 1) {
        this.items.push({ ...item });
      } else if (item.info.quantity > 1) {
        for (let i = 0; i < item.info.quantity; i++) {
          this.items.push({ ...JSON.parse(JSON.stringify(item)), index: i + 1 });
        }
      }
    });
  }

  private mergeAnswers(): void {
    for (const iItem in this.items) {
      const item = this.items[iItem];
      for (const iSection in item.sections) {
        const section = item.sections[iSection];
        for (const iQuestion in section.questions) {
          const question = section.questions[iQuestion];
          if (question.repeat && question.repeat.length) {
            question.answer = this.answers.filter(ans => ans.questionId === question.id);
          } else {
            const buildingIndex = item.index;
            const answer = this.answers.find(ans => {
              const indexOk = !buildingIndex || ans.buildingIndex === buildingIndex;
              return ans.questionId === question.id && indexOk;
            });
            if (answer) question.answer = answer;
          }
          const answers = Array.isArray(question.answer) ? question.answer : [question.answer];
          answers.forEach(answer => {
            if (answer) {
              answer.question = question;
              [...answer.flags, ...answer.comments].forEach((x: any) => {
                x.deeplink = [+iItem, +iSection, +iQuestion];
              });
            }
          });
        }
      }
    }
  }

  private getFlagsAndComments(): void {
    this.allFlags = [];
    this.allComments = [];
    for (const iItem in this.items) {
      const item = this.items[iItem];
      for (const iSection in item.sections) {
        const section = item.sections[iSection];
        const sectionFlags = [];
        const sectionComments = [];
        for (const iQuestion in section.questions) {
          const question = section.questions[iQuestion];
          const flags = this.getFlags(question);
          const comments = this.getComments(question);
          sectionFlags.push(...flags.map(f => ({ ...f, flag: f, question: question.text })));
          sectionComments.push(...comments.map(f => ({ ...f, question: question.text })));
        }
        if (sectionFlags.length) this.allFlags.push(sectionFlags);
        if (sectionComments.length) this.allComments.push(sectionComments);
      }
    }
  }

  // --- Note creation (same as InspectionNotesComponent) ---

  private async createInspectionNote(): Promise<void> {
    const cutoffDate = moment('2025-09-26').startOf('day');
    const inspectionDate = moment(this.inspection.createdAt).startOf('day');
    if (inspectionDate.isAfter(cutoffDate)) return;

    const inspectionChanges = await this.inspectionService.changes(this.inspection.id);
    let createdBy = inspectionChanges[0]?.username || 'Unknown';
    for (const change of inspectionChanges) {
      if (change.changes && change.changes.notes) createdBy = change.username;
    }
    this.allNotes.push({
      inspectionId: this.inspection.id,
      date: this.inspection.createdAt,
      type: 'inspection',
      note: this.inspection.notes,
      user: createdBy,
    });
  }

  private getCommentUser(comment: any): User | string {
    const inspector = this.inspectors.find((insp: any) => insp.id === comment.userId);
    if (inspector) {
      return { name: inspector.name, type: inspector.type, id: inspector.id } as User;
    }
    return comment.username;
  }

  private createAnswerNotes(): void {
    for (const comments of this.allComments) {
      for (const comment of comments) {
        const index = comment.deeplink;
        let commentText = comment.text === ' ' ? null : comment.text;
        if (comment.qIndex) commentText = `${commentText} - Building #${comment.qIndex}`;
        this.allNotes.push({
          inspectionId: this.inspection.id,
          date: comment.date,
          type: 'answer',
          note: commentText,
          user: this.getCommentUser(comment),
          itemIndex: index[0],
          sectionIndex: index[1],
          questionIndex: index[2],
          questionId: comment.questionId,
        });
      }
    }
  }

  private createFlagNotes(): void {
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        const index = flag.deeplink;
        if (flag.text && flag.text.length > 0) {
          let flagDate = flag.date;
          const flagUser = flag.user ? this.getUserDisplayName(flag.user) : 'Auto flag';
          for (const change of flag.changes || []) {
            if (change.changes && change.changes.Text) flagDate = change.date;
          }
          this.allNotes.push({
            inspectionId: this.inspection.id,
            date: flagDate,
            type: 'flag',
            note: flag.text,
            user: flagUser,
            itemIndex: index[0],
            sectionIndex: index[1],
            questionIndex: index[2],
            questionId: flag.questionId,
          });
        }
      }
    }
  }

  private createFlagGroupNotes(): void {
    const flagsWithViolation = [];
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        if (flag.violation?.internalNotes?.length > 0) {
          flagsWithViolation.push({ ...flag });
        }
      }
    }
    const flagsWithGroup = flagsWithViolation.filter(f => f.groupId);
    const flagsWithoutGroup = flagsWithViolation.filter(f => !f.groupId);
    const uniqueFlagsWithGroup = uniqBy(flagsWithGroup, flag => `${flag.groupId}_${flag.violation.internalNotes}`);
    const combined = [...uniqueFlagsWithGroup, ...flagsWithoutGroup];

    for (const flag of combined) {
      let flagDate = flag.date;
      const flagUser = flag.user ? this.getUserDisplayName(flag.user) : 'Auto flag';
      for (const change of flag.changes || []) {
        if (change.changes?.['Internal Notes']?.after?.length > 0) flagDate = change.date;
      }
      this.allNotes.push({
        inspectionId: this.inspection.id,
        date: flagDate,
        type: flag.groupId ? 'flag-group' : 'flag-internal',
        note: flag.violation.internalNotes,
        user: flagUser,
        flagId: flag.id,
        groupId: flag.groupId || null,
      });
    }
  }

  private createUnderwritingReviewNotes(): void {
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        if (flag.comments?.length > 0) {
          this.allNotes.push({
            inspectionId: this.inspection.id,
            date: flag.comments[0].date,
            type: 'flag-review',
            note: flag.comments[0].text,
            user: flag.comments[0].username,
            flagId: flag.id,
          });
        }
      }
    }
  }

  private async loadNotesFromApi(): Promise<void> {
    try {
      const apiNotes = await this.inspectionService.getNotes(this.inspection.id);
      const converted: JournalNote[] = (apiNotes || []).map((note: any) => ({
        id: note.id,
        inspectionId: note.inspectionId,
        date: new Date(note.createdAt || note.date),
        type: note.reason === 'Journal Note' || note.reason === 'Journal' ? 'journal' : 'scheduling',
        reason: note.reason === 'Journal Note' || note.reason === 'Journal' ? 'Journal' : note.reason,
        note: note.content || note.note,
        user: note.user,
      }));
      this.allNotes = [...this.allNotes, ...converted];
    } catch (e) {
      console.error('Error loading notes from API:', e);
    }
  }

  // --- Helpers for extracting flags/comments from questions ---

  private getComments(q: any): any[] {
    if (!q?.answer) return [];
    if (Array.isArray(q.answer)) {
      let i = 1;
      return q.answer.reduce(
        (acc, cur) => [...acc, ...cur.comments.map(c => ({ ...c, questionId: q.id, qIndex: i++ }))],
        [],
      );
    }
    return q.answer.comments.map(c => ({ ...c, questionId: q.id }));
  }

  private getFlags(q: any): any[] {
    if (!q?.answer) return [];
    if (Array.isArray(q.answer)) {
      return q.answer.reduce(
        (acc, ans) => [...acc, ...ans.flags.filter(f => f.answerId === ans.id).map(f => ({ ...f, questionId: q.id }))],
        [],
      );
    }
    return q.answer.flags.filter(f => f.answerId === q.answer.id).map(f => ({ ...f, questionId: q.id }));
  }

  // --- Arrange & Filter ---

  private arrangeNotes(): void {
    this.allNotes = this.allNotes.map(note => ({
      ...note,
      date: note.date instanceof Date ? note.date : new Date(note.date),
    }));
    this.allNotes = orderBy(this.allNotes, ['date'], ['desc']);
    this.noteTypes.forEach(t => {
      t.count = this.allNotes.filter(n => n.type === t.type && n.note != null && n.note !== '').length;
    });
    this.filterNotes();
  }

  filterNotes(): void {
    if (this.selectedTypes.length === 0) {
      this.filteredNotes = [...this.allNotes];
    } else {
      this.filteredNotes = this.allNotes.filter(n => this.selectedTypes.includes(n.type));
    }
    this.cdr.detectChanges();
  }

  toggleNoteType(type: string): void {
    const idx = this.selectedTypes.indexOf(type);
    if (idx > -1) {
      this.selectedTypes.splice(idx, 1);
    } else {
      this.selectedTypes.push(type);
    }
    this.filterNotes();
  }

  get isFilterChanged(): boolean {
    return !(this.selectedTypes.length === 1 && this.selectedTypes[0] === 'journal');
  }

  clearFilter(): void {
    this.selectedTypes = ['journal'];
    this.filterNotes();
  }

  toggleFilterDropdown(): void {
    this.showFilterDropdown = !this.showFilterDropdown;
  }

  getFilterText(): string {
    if (this.selectedTypes.length === 0) return 'Filter by Type';
    if (this.selectedTypes.length === 1) {
      const type = this.noteTypes.find(t => t.type === this.selectedTypes[0]);
      return type ? type.label : 'Filter by Type';
    }
    return `${this.selectedTypes.length} types selected`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-dropdown-wrapper')) {
      this.showFilterDropdown = false;
    }
  }

  // --- Panel actions ---

  onClose(): void {
    this.closePanel.emit();
  }

  openAddModal(): void {
    this.editingNote = null;
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
    this.editingNote = null;
  }

  openEditModal(note: JournalNote): void {
    this.editingNote = note;
    this.showAddModal = true;
  }

  onEntryCreated(createdNote: any): void {
    const entry: JournalNote = {
      id: createdNote.id,
      inspectionId: createdNote.inspectionId,
      date: new Date(createdNote.createdAt || createdNote.date),
      type: createdNote.reason === 'Journal Note' || createdNote.reason === 'Journal' ? 'journal' : 'scheduling',
      reason:
        createdNote.reason === 'Journal Note' || createdNote.reason === 'Journal' ? 'Journal' : createdNote.reason,
      note: createdNote.content || createdNote.note,
      user: createdNote.user,
    };
    this.allNotes.unshift(entry);
    this.toastr.success('Note saved successfully!');
    this.arrangeNotes();
    this.showAddModal = false;
    this.cdr.detectChanges();
  }

  onEntryUpdated(updatedNote: any): void {
    const existing = this.allNotes.find(n => n.id === updatedNote.id);
    if (existing) {
      existing.note = updatedNote.content || updatedNote.note;
      existing.reason = updatedNote.reason;
    }
    this.editingNote = null;
    this.showAddModal = false;
    this.toastr.success('Note saved successfully!');
    this.filterNotes();
    this.cdr.detectChanges();
  }

  deleteNote(note: JournalNote): void {
    this.dialog.confirm().show({
      message: 'Are you sure you want to delete this note?',
      confirmed: async () => {
        try {
          await this.inspectionService.deleteNote(this.inspectionId, note.id);
          this.allNotes = this.allNotes.filter(n => n.id !== note.id);
          this.arrangeNotes();
          this.toastr.success('Journal deleted');
          this.cdr.detectChanges();
        } catch (e) {
          console.error('Error deleting note:', e);
        }
      },
    });
  }

  // --- Display helpers ---

  formatDate(date: Date): string {
    return moment(date).format('MM-DD-YYYY');
  }

  formatTime(date: Date): string {
    return moment(date).format('hh:mm A');
  }

  getNoteTypeLabel(type: string): string {
    const typeMap = {
      inspection: 'Inspection Created Notes',
      answer: 'Question Comments/Notes',
      flag: 'Flag Notes',
      'flag-group': 'Flag Group Internal Notes',
      'flag-internal': 'Flag Internal Notes',
      'flag-review': 'Underwriting Review Comments',
      scheduling: 'Scheduling Notes',
      journal: 'Journal Notes',
    };
    return typeMap[type] || '';
  }

  getUserDisplayName(user: User | string | undefined): string {
    if (!user) return 'Unknown User';
    if (typeof user === 'string') return user;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.name) return user.name;
    return 'Unknown User';
  }

  getNoteTypeValue(user: User | string | undefined): number {
    if (!user || typeof user === 'string') return 0;
    if ((user as any).type === 'Inspector') return 1;
    if ((user as any).type === 'Vendor') return 2;
    return 0;
  }

  getTagIconSrc(typeValue: number): string {
    return typeValue === 1 ? 'assets/img/inspector-note.svg' : 'assets/img/admin-note.svg';
  }

  getTagIconClass(typeValue: number): string {
    return typeValue === 2 ? 'vendor-icon' : '';
  }

  getTagTextClass(typeValue: number): string {
    if (typeValue === 2) return 'vendor-text';
    return typeValue === 0 ? 'admin-text' : 'inspector-text';
  }

  getTagText(typeValue: number): string {
    if (typeValue === 2) return 'Vendor note';
    return typeValue === 0 ? 'Admin note' : 'Inspector note';
  }

  canEditNote(note: JournalNote): boolean {
    if (note.type !== 'journal') return false;
    if (!this.currentUser || !note.user) return false;
    if (typeof note.user === 'string') return false;
    return note.user.id === this.currentUser.id;
  }

  shouldShowReadMore(text: string): boolean {
    return !!text && text.trim().length > 100;
  }

  getTruncatedText(text: string): string {
    if (!text) return '';
    return text.trim().length <= 100 ? text : text.substring(0, 100) + '...';
  }

  getTruncatedMetadataText(text: string): string {
    if (!text) return '';
    return text.trim().length <= 15 ? text : text.substring(0, 15) + '...';
  }

  isExpanded(note: JournalNote): boolean {
    return this.expandedNotes.has(this.createNoteKey(note));
  }

  toggleNoteExpansion(note: JournalNote): void {
    const key = this.createNoteKey(note);
    if (this.expandedNotes.has(key)) {
      this.expandedNotes.delete(key);
    } else {
      this.expandedNotes.add(key);
    }
  }

  private createNoteKey(note: JournalNote): string {
    const type = note.type || 'unknown';
    const content = note.note || '';
    const date = note.date ? note.date.toString() : '';
    const user = this.getUserDisplayName(note.user);
    return `${type}_${content.substring(0, 50)}_${date}_${user}`.replace(/\s+/g, '_');
  }
}
